/**
 * ReportSeriesView - Main Archive View
 *
 * Displays report series (20-50 items), NOT individual snapshots (1500+).
 * This is the entry point to the 3-level report architecture.
 */

import React, { useState } from 'react';
import {
  Archive,
  Pin,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useReportSeries } from '../../../hooks/useReportSeries.js';
import { CATEGORY_CONFIG } from '../../../hooks/useReportTemplates.js';
import ReportSeriesCard from './ReportSeriesCard.jsx';
import ReportSeriesFilters from './ReportSeriesFilters.jsx';

export default function ReportSeriesView({ onNavigate }) {
  const { t } = useTranslation();
  const {
    series,
    pinnedSeries,
    sortedCategories,
    stats,
    loading,
    error,
    filters,
    loadSeries,
    togglePin,
    updateFilter,
    resetFilters
  } = useReportSeries();

  const [expandedCategories, setExpandedCategories] = useState(new Set()); // All closed by default
  const [, setSelectedSeriesId] = useState(null);

  // Toggle category expansion
  const toggleCategory = (category) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  // Handle series selection (navigate to snapshot history)
  const handleSelectSeries = (seriesId) => {
    setSelectedSeriesId(seriesId);
    if (onNavigate) {
      onNavigate('report-series-detail', { seriesId });
    }
  };

  // Render loading state
  if (loading && series.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        <span className="ml-3 text-gray-600">{t('reports.series.loading')}</span>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-96 text-red-500">
        <AlertCircle className="w-12 h-12 mb-4" />
        <p className="text-lg font-medium">{t('common.states.loadError')}</p>
        <p className="text-sm text-gray-500 mt-2">{error}</p>
        <button
          onClick={loadSeries}
          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
        >
          {t('common.actions.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Archive className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{t('reports.series.title')}</h1>
              <p className="text-sm text-gray-500">
                {t('reports.series.seriesCount', { count: stats.totalSeries })} • {t('reports.series.snapshotsCount', { count: stats.totalSnapshots })}
              </p>
            </div>
          </div>

          <button
            onClick={loadSeries}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            {t('common.actions.refresh')}
          </button>
        </div>
      </div>

      {/* Filters */}
      <ReportSeriesFilters
        filters={filters}
        onUpdateFilter={updateFilter}
        onReset={resetFilters}
        stats={stats}
      />

      {/* Pinned Series */}
      {pinnedSeries.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Pin className="w-5 h-5 text-amber-500" />
            <h2 className="text-lg font-semibold text-gray-900">{t('reports.series.pinned')}</h2>
            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
              {pinnedSeries.length}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pinnedSeries.map(s => (
              <ReportSeriesCard
                key={s.id}
                series={s}
                onSelect={() => handleSelectSeries(s.id)}
                onTogglePin={() => togglePin(s.id)}
                isPinned={true}
              />
            ))}
          </div>
        </div>
      )}

      {/* Series by Category */}
      <div className="space-y-6">
        {sortedCategories.map(({ key, series: categorySeries }) => {
          const categoryConfig = CATEGORY_CONFIG[key] || {
            label: 'Autres',
            color: 'gray'
          };
          const isExpanded = expandedCategories.has(key);

          return (
            <div key={key} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(key)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full bg-${categoryConfig.color}-500`}></div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t(`reports.series.categories.${key}`, { defaultValue: categoryConfig.label })}
                  </h3>
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-sm rounded-full">
                    {t('reports.series.seriesCount', { count: categorySeries.length })}
                  </span>
                </div>
                {isExpanded ? (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                )}
              </button>

              {/* Category Content */}
              {isExpanded && (
                <div className="border-t border-gray-100 p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {categorySeries.map(s => (
                      <ReportSeriesCard
                        key={s.id}
                        series={s}
                        onSelect={() => handleSelectSeries(s.id)}
                        onTogglePin={() => togglePin(s.id)}
                        isPinned={s.is_pinned}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {series.length === 0 && !loading && (
        <div className="flex flex-col items-center justify-center py-16 text-gray-500">
          <Archive className="w-16 h-16 mb-4 text-gray-300" />
          <p className="text-lg font-medium">{t('reports.series.emptyTitle')}</p>
          <p className="text-sm mt-2">{t('reports.series.emptyHint')}</p>
        </div>
      )}
    </div>
  );
}
